import { ActorData } from "@/types/socket";


export interface WebSocketData {
  data: ActorData | null;
  isConnected: boolean;
  error: string | null;
}

type WebSocketEventHandler = (data: WebSocketData) => void;

/**
 * Static WebSocket service for managing real-time actor data from AWS API Gateway
 * 
 * @example
 * ```typescript
 * // Subscribe to updates
 * const unsubscribe = WebSocketService.subscribe((data) => {
 *   console.log('Connected:', data.isConnected);
 *   console.log('Actors:', data.data?.actors.length);
 * });
 * 
 * // Start connection
 * WebSocketService.connect();
 * 
 * // Send JSON payload
 * const success = WebSocketService.send({
 *   type: 'command',
 *   action: 'updatePosition',
 *   data: { x: 100, y: 200 }
 * });
 * 
 * // Send subscription with JWT token
 * const subscriptionSuccess = WebSocketService.sendSubscription(jwtToken, 'prod');
 * 
 * // Cleanup
 * unsubscribe();
 * WebSocketService.disconnect();
 * ```
 */
export class WebSocketService {
  private static instance: WebSocketService | null = null;
  private ws: WebSocket | null = null;
  private isConnecting = false;
  private subscribers = new Set<WebSocketEventHandler>();
  private reconnectTimeoutId: NodeJS.Timeout | null = null; // Track how many components are using the service
  
  private data: ActorData | null = null;
  private isConnected = false;
  private error: string | null = null;

  private constructor() {}

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Subscribe to WebSocket data updates
   */
  static subscribe(handler: WebSocketEventHandler): () => void {
    const instance = WebSocketService.getInstance();
    instance.subscribers.add(handler);
    
    // Immediately call with current state
    handler({
      data: instance.data,
      isConnected: instance.isConnected,
      error: instance.error,
    });

    // Return unsubscribe function
    return () => {
      instance.subscribers.delete(handler);
    };
  }

  /**
   * Connect to the WebSocket
   */
  static connect(onConnected?: () => void): void {
    const instance = WebSocketService.getInstance();
    instance.connect(true, onConnected);
  }

  /**
   * Disconnect from the WebSocket
   */
  static disconnect(): void {
    const instance = WebSocketService.getInstance();
    instance.disconnect();
  }

  /**
   * Manually reconnect to the WebSocket
   */
  static reconnect(): void {
    const instance = WebSocketService.getInstance();
    instance.reconnect();
  }

  /**
   * Get current connection state
   */
  static getState(): WebSocketData {
    const instance = WebSocketService.getInstance();
    return {
      data: instance.data,
      isConnected: instance.isConnected,
      error: instance.error,
    };
  }

  /**
   * Send JSON payload through WebSocket
   */
  static send(payload: Record<string, unknown> | unknown[]): boolean {
    const instance = WebSocketService.getInstance();
    return instance.send(payload);
  }


  private connect(clearError = true, onConnected?: () => void): void {
    // Prevent multiple simultaneous connections
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket connection already in progress, skipping...');
      return;
    }

    // If we already have a connected WebSocket, don't create a new one
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected, skipping...');
      return;
    }

    try {
      this.isConnecting = true;
      if (clearError) {
        this.setError(null);
      }
      
      const websocket = new WebSocket(process.env.WEBSOCKET_EVENT_URL || '');
      
      websocket.onopen = () => {
        console.log('WebSocket connected to AWS API Gateway');
        this.setConnected(true);
        this.setError(null);
        this.isConnecting = false;
        if (onConnected) {
          onConnected();
        }
      };

      websocket.onmessage = (event) => {
        try {
          if(event.data === 'subscribed') {
            console.log('WebSocket subscribed');
            return;
          }
          if(event.data === 'missing topic') {
            console.log('Missing topic');
            return;
          }
          // Parse the incoming message
          const messageData = JSON.parse(event.data);
          
          // Validate that the data matches our expected structure
          if (messageData && typeof messageData === 'object') {
            // If the message has the expected structure, use it directly
            if (messageData.actors && Array.isArray(messageData.actors)) {
              this.setData(messageData);
            } else {
              // If the message structure is different, try to extract or transform it
              console.warn('Received data with unexpected structure:', messageData);
            }
          }
        } catch (parseError) {
          console.error('Error parsing WebSocket message:', parseError);
          this.setError(event.data);
        }
      };

      websocket.onclose = (event) => {
        const closeInfo = this.getCloseCodeInfo(event.code);
        console.log(`WebSocket disconnected: ${closeInfo.name} (${event.code}) - ${event.reason || closeInfo.description}`);
        
        this.setConnected(false);
        this.isConnecting = false;
        
        // Handle different close codes
        if (event.code === 1000) {
          // Normal closure - no error, no reconnect
          console.log('WebSocket closed normally');
          this.setError(null);
          return; // Don't schedule reconnect for normal closure
        } else if (event.code === 1001) {
          // Going away - server restart or browser navigation
          this.setError('Server is going away (restarting)');
        } else if (event.code === 1002) {
          // Protocol error - don't reconnect immediately
          this.setError('Protocol error - check WebSocket implementation');
          this.scheduleReconnect(10000); // Longer delay for protocol errors
          return;
        } else if (event.code === 1003) {
          // Unsupported data - don't reconnect
          this.setError('Unsupported data type received');
          return;
        } else if (event.code === 1005) {
          // No status received - connection closed without proper close code
          this.setError('Connection closed without status (possible network issue)');
          return;
        } else if (event.code === 1006) {
          // Abnormal closure - network issues
          this.setError('Connection lost (network issue)');
        } else if (event.code === 1007) {
          // Invalid frame payload data - don't reconnect
          this.setError('Invalid data received');
          return;
        } else if (event.code === 1008) {
          // Policy violation - don't reconnect
          this.setError('Policy violation - check server configuration');
          return;
        } else if (event.code === 1009) {
          // Message too big - don't reconnect
          this.setError('Message too large');
          return;
        } else if (event.code === 1010) {
          // Missing extension - don't reconnect
          this.setError('Required extension missing');
          return;
        } else if (event.code === 1011) {
          // Internal error - server issue
          this.setError('Server internal error');
        } else if (event.code === 1012) {
          // Service restart - server restarting
          this.setError('Service restarting');
        } else if (event.code === 1013) {
          // Try again later - temporary server issue
          this.setError('Service temporarily unavailable');
        } else if (event.code === 1014) {
          // Bad gateway - proxy issue
          this.setError('Bad gateway - proxy issue');
        } else if (event.code === 1015) {
          // TLS handshake failure - don't reconnect
          this.setError('TLS handshake failed');
          return;
        } else if (event.code >= 4000 && event.code < 5000) {
          // Custom application errors
          this.setError(`Application error: ${event.reason || 'Unknown application error'}`);
        } else {
          // Unknown or other codes
          this.setError(`Connection closed: ${event.reason || closeInfo.description}`);
        }
        
        this.scheduleReconnect();
      };

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.setError('WebSocket connection error');
        this.setConnected(false);
        this.isConnecting = false;
        this.scheduleReconnect();
      };

      this.ws = websocket;
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      this.setError('Failed to establish WebSocket connection');
      this.isConnecting = false;
    }
  }

  private disconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.setConnected(false);
    this.isConnecting = false;
  }

  private reconnect(): void {
    this.disconnect();
    this.connect(false); // Don't clear error on reconnect
  }

  private scheduleReconnect(delay = 3000): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }
    
    this.reconnectTimeoutId = setTimeout(() => {
      console.log('Attempting to reconnect...');
      this.reconnect();
    }, delay);
  }

  private getCloseCodeInfo(code: number): { name: string; description: string } {
    const closeCodes: Record<number, { name: string; description: string }> = {
      1000: { name: 'Normal Closure', description: 'Connection closed normally' },
      1001: { name: 'Going Away', description: 'Server is going away or browser is navigating away' },
      1002: { name: 'Protocol Error', description: 'Protocol error occurred' },
      1003: { name: 'Unsupported Data', description: 'Received unsupported data type' },
      1004: { name: 'Reserved', description: 'Reserved for future use' },
      1005: { name: 'No Status Received', description: 'No status code was actually present' },
      1006: { name: 'Abnormal Closure', description: 'Connection was closed abnormally' },
      1007: { name: 'Invalid Frame Payload Data', description: 'Received invalid UTF-8 data' },
      1008: { name: 'Policy Violation', description: 'Policy violation occurred' },
      1009: { name: 'Message Too Big', description: 'Message is too big to process' },
      1010: { name: 'Missing Extension', description: 'Client expected server to negotiate extension' },
      1011: { name: 'Internal Error', description: 'Server encountered an unexpected condition' },
      1012: { name: 'Service Restart', description: 'Server is restarting' },
      1013: { name: 'Try Again Later', description: 'Server is overloaded' },
      1014: { name: 'Bad Gateway', description: 'Gateway received invalid response' },
      1015: { name: 'TLS Handshake', description: 'TLS handshake failure' },
    };

    return closeCodes[code] || { 
      name: 'Unknown', 
      description: code >= 4000 && code < 5000 
        ? 'Custom application error' 
        : 'Unknown close code' 
    };
  }

  private setData(data: ActorData | null): void {
    this.data = data;
    this.notifySubscribers();
  }

  private setConnected(isConnected: boolean): void {
    this.isConnected = isConnected;
    this.notifySubscribers();
  }

  private setError(error: string | null): void {
    this.error = error;
    this.notifySubscribers();
  }

  private send(payload: Record<string, unknown> | unknown[]): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not connected. Cannot send message.');
      return false;
    }

    try {
      const jsonString = JSON.stringify(payload);
      this.ws.send(jsonString);
      console.log('WebSocket message sent:', payload);
      return true;
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      return false;
    }
  }

  private notifySubscribers(): void {
    const state: WebSocketData = {
      data: this.data,
      isConnected: this.isConnected,
      error: this.error,
    };
    
    this.subscribers.forEach(handler => {
      try {
        handler(state);
      } catch (error) {
        console.error('Error in WebSocket subscriber:', error);
      }
    });
  }
}

